import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { LOG_API, type Log } from '@chess/api-log';
import { REVIEW_API, type Review } from '@chess/api-review';
import { STORE_API, type GameStore } from '@chess/api-model';
import { SCAN_API, type ScanService } from '@chess/api-scan';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { SYNC_API, type SyncService } from '@chess/api-sync';
import { bridgeUrl, connect, tabIdFrom, type Connection } from './connection.ts';
import { handleCall, parseCall, type CallContext } from './handlers.ts';
import { Deduper, debounce, newToasts, reviewSummary, scanSummary, storeSummary, syncSummary, STORE_DEBOUNCE_MS } from './events.ts';
import { hostOf } from './host.ts';
import { CoachSettingsStore } from './settings.ts';
import { StatusStore } from './status.ts';
import { CoachForm, type SaveStatus } from './CoachForm.tsx';
import './settings-coach.css';

// The page's end of the AI bridge: the Claude session in `ai/` coaches the
// user on their games by driving the running app. Claude talks to the bridge
// server (`ai/server`, loopback) over MCP, the server talks to this plugin
// over a WebSocket, and this plugin answers its named commands from the
// plugin registry (`commands.ts`) and streams a few events. It runs in every
// build (the user, 2026-10-20: "both dev and prod versions will connect to
// the local server"), but dials only when the Coach switch in Settings is on,
// and never retries on its own ("there's no need to retry. maybe we can add
// the slider in the settings page"): once on load, once when the switch is
// turned on, once per press of Connect. Evaluating an expression stays a dev
// server thing. See docs/architecture/core/plugin-shared-ai-bridge.md.

export function setup(app: PiletApi) {
  const log = app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name));
  const debug = (message: string, fields?: Record<string, unknown>) => void log.then((l) => l.debug(message, fields));

  const storage = app.getApi<Storage>(STORAGE_API).then((s) => s.scope('settings'));
  const settings = new CoachSettingsStore(storage);
  const status = new StatusStore();

  // `window.__host` is set after every setup has run (main.tsx), on the dev
  // server only, so it is read per call, never kept; elsewhere the plugin's
  // own registry handle answers.
  const api = (name: string): unknown => hostOf(window)?.registry.tryGetApi(name) ?? app.tryGetApi(name);
  const context: CallContext = {
    scope: () => {
      const host = hostOf(window);
      return host ? { registry: host.registry, host } : undefined;
    },
    evalAllowed: import.meta.env.DEV,
    commands: {
      api,
      pathname: () => location.pathname,
      navigate: (path) => {
        history.pushState(null, '', path);
        dispatchEvent(new PopStateEvent('popstate', { state: null }));
      },
    },
  };

  const tabId = tabIdFrom(typeof sessionStorage === 'undefined' ? undefined : sessionStorage, () => crypto.randomUUID());
  const url = bridgeUrl(import.meta.env.VITE_AI_BRIDGE_PORT);
  let connection: Connection | undefined;
  let subscribed = false;

  // The events go through whichever connection is up; nothing is emitted
  // before the first welcome subscribes.
  const events = new Deduper((event) => void connection?.send(event));

  /** One dial. A connection already up is closed first, so there is never more than one. */
  const dial = () => {
    connection?.close();
    status.set({ kind: 'connecting' });
    const c = connect({
      url,
      hello: () => ({ type: 'hello', tabId, url: location.href, title: document.title }),
      debug,
      onMessage: (text) => {
        const call = parseCall(text);
        if (!call) return;
        void handleCall(call, context).then((reply) => c.send(reply));
      },
      onOpen: () => {
        if (connection !== c) return;
        status.set({ kind: 'connected', since: Date.now() });
        if (subscribed) {
          events.resend();
          return;
        }
        subscribed = true;
        subscribe();
      },
      onClose: (reason) => {
        if (connection !== c) return;
        connection = undefined;
        status.set({ kind: 'disconnected', reason });
      },
    });
    connection = c;
  };

  /** The switch turned off: the socket closes, the status is Off. */
  const hangUp = () => {
    const c = connection;
    connection = undefined;
    c?.close();
    status.set({ kind: 'off' });
  };

  // On load: one dial when the switch is on.
  void settings.load().then((s) => {
    if (s.enabled) dial();
  });

  // The subscriptions, made once, on the first welcome, and kept across
  // later connections; the deduper's memory gives a new connection the
  // current state (`resend`).
  const subscribe = () => {
    void app.getApi<Review>(REVIEW_API).then((review) => {
      events.emit('review', reviewSummary(review.state()));
      review.subscribe((state) => void events.emit('review', reviewSummary(state)));
    });
    void app.getApi<GameStore>(STORE_API).then((store) => {
      const send = () => void events.emit('store', storeSummary(store.getState()));
      send();
      store.subscribe(debounce(send, STORE_DEBOUNCE_MS));
    });
    void app.getApi<ScanService>(SCAN_API).then((scan) => {
      const send = () => void events.emit('scan', scanSummary(scan.progress()));
      send();
      scan.subscribe(send);
    });
    void app.getApi<SyncService>(SYNC_API).then((sync) => {
      const send = () => void events.emit('sync', syncSummary(sync.state()));
      send();
      sync.subscribe(send);
    });
    // Toasts: the `toast` API has no observer; every toast is a host
    // notification, and the registry tells its watchers of each change.
    // The host is exposed on the dev server only, so a production page
    // sends no toast events.
    const host = hostOf(window);
    if (host) {
      let seen = host.registry.notifications().reduce((m, n) => Math.max(m, n.id), 0);
      host.registry.subscribe(() => {
        const next = newToasts(host.registry.notifications(), seen);
        seen = next.seen;
        for (const toast of next.toasts) events.push('toast', toast);
      });
    }
    app.on('review.puzzleSolved', (e) => events.push('puzzle', { outcome: 'solved', uci: e.uci }));
    app.on('review.puzzleFailed', (e) => events.push('puzzle', { outcome: 'failed', uci: e.uci }));
  };

  // The Coach section of Settings: the switch, the status, Connect.
  const Section: React.FC = () => {
    const current = React.useSyncExternalStore(
      (l) => settings.subscribe(() => l()),
      () => settings.get(),
      () => settings.get(),
    );
    const connectionStatus = React.useSyncExternalStore(
      (l) => status.subscribe(l),
      () => status.get(),
      () => status.get(),
    );
    const [loaded, setLoaded] = React.useState(false);
    const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('loading');
    const [error, setError] = React.useState<string | undefined>();
    React.useEffect(() => {
      let live = true;
      void settings.load().then(() => {
        if (!live) return;
        setLoaded(true);
        setSaveStatus('idle');
      });
      return () => {
        live = false;
      };
    }, []);
    const onChange = (next: boolean) => {
      setSaveStatus('saving');
      setError(undefined);
      settings.save({ enabled: next }).then(
        () => {
          setSaveStatus('saved');
          if (next) dial();
          else hangUp();
        },
        (e) => {
          setSaveStatus('error');
          setError(e instanceof Error ? e.message : String(e));
          void log.then((l) => l.error('the coach setting could not be saved', e));
        },
      );
    };
    return <CoachForm enabled={loaded ? current.enabled : undefined} status={connectionStatus} saveStatus={saveStatus} error={error} onChange={onChange} onConnect={dial} />;
  };
  app.registerExtension('settings.sections', Section, { order: 27 });
}
